import axios from 'axios';
import braintree from 'braintree-web';
import paypal from 'paypal-checkout';
import { useEffect } from 'react';
import { useStorageState } from 'react-storage-hooks';

import * as errors from 'utils/errors';
import * as helpers from 'utils/helpers';

// TODO: handle case with the Paypal=checout useEffect hook below
/* eslint-disable */

export type Props = {
  /** The base url that will used for auth token api call */
  baseUrl: string;
  /** The merchant id for the domain */
  merchantAccountId: string;
  /** The type of payment gateway (eg braintree = BT) */
  type: 'BT';
  /** The quoteId for what is being purchased */
  quoteId: string;
  /** The ID of the customer who is making purchase */
  customerId: string;
  /** The email to send the receipt to once the payment has been made */
  receiptEmail: string;
  /** The amount to charge the paypal account */
  amount: number;
  /** Optional store paypal account details into user's account. Defaults to false */
  saveForLater?: boolean;

  /**
   * Optional access token
   * @remarks Required when saveForLater is enabled
   * */
  accessToken?: string;
  /** The Braintree environment to use for the payment */
  env: 'sandbox' | 'production' | 'REPLACE_BT_ENV';
  /** Handler function for successful paypal authorise before submitting to backend */
  onBeforeSubmit?: () => void;
  /** Handler function for successful paypal authorise after submitting to backend */
  onAfterSubmit?: () => void;
  /** function for customer handler for any errors */
  onError?: () => void;
  /** Optionally disable the paypal button */
  disabled?: boolean;
};
/**
 * Render a Paypal payment button inside given container <div />
 * @param id wrapper <div /> element unique "id" attribute for the button
 * @param props proeprties required for interacting with Paypal service
 */
export default function usePaypal(id: string, props: Props) {
  const [receiptEmail, setReceiptEmail, writeReceiptEmailError] = useStorageState(
    localStorage,
    'PayPal.receiptEmail',
    props.receiptEmail
  );

  /**
   * Initialize the paypal button
   */
  useEffect(() => {
    const getAuthToken = async () => {
      const { baseUrl, merchantAccountId, type } = props;
      const tokenPayload = {
        merchantAccountId: merchantAccountId,
        type: type
      };
      try {
        const response = await axios.post(
          `${baseUrl}/adapter/v1/payments/tokens/authorisation`,
          tokenPayload
        );
        if (!response.data.token) {
          throw new errors.SVError('Token not being returned by api');
        }
        return response.data.token;
      } catch (error) {
        helpers.errorMessage('Error getting token', error);
        throw error;
      }
    };
    const initButton = (paypalCheckoutInstance: any, myDeviceData: string) => {
      const {
        amount,
        env,
        onAfterSubmit,
        onBeforeSubmit,
        onError,
        disabled,
        saveForLater = false
      } = props;

      // Set up PayPal with the checkout.js library
      return {
        env: env,
        commit: true,
        style: {
          label: 'checkout',
          size: 'responsive', // small | medium | large | responsive
          shape: 'rect', // pill | rect
          color: 'silver' // gold | blue | silver | black
        },
        payment: () => {
          return paypalCheckoutInstance.createPayment({
            flow: saveForLater ? 'vault' : 'checkout',
            amount: amount,
            currency: 'AUD'
          });
        },
        onAuthorize: async (data: any, actions: any) => {
          const payload = await paypalCheckoutInstance.tokenizePayment(data);
          // Submit `payload.nonce` to your server.
          if (onBeforeSubmit) onBeforeSubmit();
          await submitPaymentToBackend(payload, myDeviceData, props);
          if (onAfterSubmit) onAfterSubmit();
        },
        validate: (actions: any) => {
          if (disabled) actions.disable();
        },
        onCancel: (data: any) => {
          // helpers.logMessage('Paypal payment cancelled', JSON.stringify(data));
        },
        onError: (err: any) => {
          helpers.errorMessage('Paypal Error', err);
          if (onError) onError();
        }
      };
    };
    const submitPaymentToBackend = async (payload: any, myDeviceData: string, props: Props) => {
      const { baseUrl, quoteId, customerId, saveForLater = false, accessToken } = props;

      if (saveForLater && !accessToken) {
        throw new errors.SVError('Access token is required when saveForLater is enabled');
      }

      const btPaypalPayload = {
        quoteID: quoteId,
        customerID: customerId,
        paymentMethod: {
          method: 'PayPal',
          paymentMethodNonce: payload.nonce
        },
        deviceData: myDeviceData.replace(/"/g, "'"),
        receiptEmail,
        isSaveAsInstrument: saveForLater
      };

      const config =
        saveForLater && accessToken
          ? {
              headers: { Authorization: `Bearer ${accessToken}` }
            }
          : {};

      try {
        await axios.post(
          `${baseUrl}/adapter/v1/payments/payments-btpaypal-proxy`,
          btPaypalPayload,
          config
        );
      } catch (error) {
        throw error;
      }
    };

    const init = async () => {
      try {
        const clientAuthToken = await getAuthToken();
        // Create a client.
        const clientInstance = await braintree.client.create({
          authorization: clientAuthToken
        });
        const dataCollectorPayload = {
          client: clientInstance,
          paypal: true,
          kount: true
        };
        const dataCollectorInstance = await braintree.dataCollector.create(dataCollectorPayload);
        const myDeviceData = dataCollectorInstance.deviceData;
        const paypayCheckoutPayload = {
          client: clientInstance
        };
        const paypalCheckoutInstance = await (braintree as any).paypalCheckout.create(
          paypayCheckoutPayload
        );
        const paypalConfig = initButton(paypalCheckoutInstance, myDeviceData);
        await (paypal.Button as any).render(paypalConfig, `#${id}`);
        // helpers.logMessage('ready!');
      } catch (error) {
        // Stop if there was a problem creating the client.
        // This could happen if there is a network error or if the authorization
        // is invalid or if there was a problem creating PayPal Checkout.
        const { onError } = props;
        helpers.errorMessage('Error PayPalButton (initBraintreePaypal) => ', error);
        if (onError) onError();
      }
    };
    setReceiptEmail(receiptEmail => receiptEmail);
    if (writeReceiptEmailError) {
      throw new errors.SVError(
        `Cannot write to localStorage value of receiptEmail ${writeReceiptEmailError.message}`
      );
    }
    init();
  }, [id]);
}
